import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '../../core/avatar';
import type { UserSuggestion } from './types';

type Props = {
  items: UserSuggestion[];
  command: (item: { userId: string; label: string }) => void;
};

export const MentionPopup = forwardRef<{ onKeyDown: (e: { event: KeyboardEvent }) => boolean }, Props>(
  ({ items, command }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => setSelectedIndex(0), [items]);

    useImperativeHandle(ref, () => ({
      onKeyDown({ event }) {
        if (event.key === 'ArrowUp') {
          setSelectedIndex((i) => (i + items.length - 1) % items.length);
          return true;
        }
        if (event.key === 'ArrowDown') {
          setSelectedIndex((i) => (i + 1) % items.length);
          return true;
        }
        if (event.key === 'Enter') {
          const item = items[selectedIndex];
          if (item) command({ userId: item.userId, label: item.displayName });
          return true;
        }
        return false;
      },
    }));

    if (items.length === 0) return null;

    return (
      <div className="z-50 min-w-[200px] rounded-lg border border-border bg-popover shadow-lg p-1">
        {items.map((item, i) => (
          <button
            key={item.userId}
            type="button"
            onClick={() => command({ userId: item.userId, label: item.displayName })}
            className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm ${i === selectedIndex ? 'bg-accent' : ''}`}
          >
            <Avatar className="h-6 w-6">
              <AvatarImage src={item.avatarUrl ?? undefined} />
              <AvatarFallback className="text-xs">{item.displayName.slice(0, 1).toUpperCase()}</AvatarFallback>
            </Avatar>
            <span className="flex-1 truncate">{item.displayName}</span>
          </button>
        ))}
      </div>
    );
  },
);

MentionPopup.displayName = 'MentionPopup';
